import { Paths } from '@/app/paths';
import { AccommodationsSearchRequestDto } from '@/features/AccommodationList/api/requestTypes';
import useIntl from '@/infrastructure/hooks/useIntl';
import { customerInfoAtom } from '@/infrastructure/state/customerInfo';
import { CustomerInfoType } from '@/types/widgetConfig';
import { useAtom } from 'jotai';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import useMelingo from './api/useMelingo';
import {
  IMessage,
  MessageStatus,
  chatMessagesAtom,
  getNewChatId
} from './state';
import { getWelcomeMessageHtml } from './welcomeMessage';

const useChatInit = () => {
  const t = useIntl('chat');
  const navigate = useNavigate();
  const [_, setChatMessages] = useAtom(chatMessagesAtom);
  const [customerInfoState, setCustomerInfoState] = useAtom(customerInfoAtom);
  const [isChatLoaded, setIsChatLoaded] = useState<boolean>();

  const { queryCall } = useMelingo();

  const init = async (
    initialRequest: AccommodationsSearchRequestDto | undefined
  ) => {
    await loadChat(customerInfoState.customerInfo, initialRequest);
  };

  const loadChat = async (
    customerInfo: CustomerInfoType | undefined,
    searchRequest: AccommodationsSearchRequestDto | undefined
  ) => {
    setIsChatLoaded(false);
    navigate(Paths.Main);

    const newChatId = getNewChatId();

    setChatMessages({
      chatId: newChatId,
      messages: []
    });

    if (customerInfo && Object.keys(customerInfo).length !== 0) {
      // fire and forget - agent only needs the context, the welcome message is static
      queryCall({
        chat_id: newChatId,
        message: `customerInfo: ${JSON.stringify(customerInfo)}`
      }).catch(ex => {
        console.debug('customer info could not be sent to agent', ex);
      });
    }

    const responseMessage: IMessage = {
      id: `${Date.now()}-S`,
      isUserMessage: false,
      message: getWelcomeMessageHtml(t, searchRequest),
      status: MessageStatus.Received,
      contextRequest: { searchRequest }
    };

    setChatMessages(chatMessages => ({
      chatId: chatMessages.chatId,
      messages: [responseMessage, ...chatMessages.messages]
    }));

    setIsChatLoaded(true);
  };

  const restartChat = async () => {
    setCustomerInfoState({ isInitialized: true });
    await loadChat(undefined, undefined);
  };

  return {
    isChatLoaded,
    init,
    restartChat
  };
};

export default useChatInit;
